import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon } from 'lucide-react';

/**
 * ThemeToggle — levetta animata "liquid" per passare da scuro a chiaro.
 * Usa transizioni fluide con cubic-bezier per effetto gel/liquido.
 */
export default function ThemeToggle({ className = '' }) {
  const { theme, toggle } = useTheme();
  const isLight = theme === 'light';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isLight}
      aria-label={isLight ? 'Passa al tema scuro' : 'Passa al tema chiaro'}
      data-testid="theme-toggle"
      onClick={toggle}
      className={
        'theme-liquid-toggle relative inline-flex items-center h-9 w-[72px] rounded-full ' +
        'border border-white/10 overflow-hidden select-none ' +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-chartreuse/60 ' +
        className
      }
      style={{
        background: isLight
          ? 'linear-gradient(135deg, rgba(180,255,0,0.18), rgba(255,230,160,0.25))'
          : 'linear-gradient(135deg, rgba(10,14,16,0.9), rgba(30,34,36,0.9))',
      }}
    >
      {/* Track icons */}
      <span className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none z-0">
        <Sun size={14} className={`transition-opacity duration-500 ${isLight ? 'opacity-80 text-amber-500' : 'opacity-40 text-amber-300/60'}`} strokeWidth={1.8}/>
      </span>
      <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none z-0">
        <Moon size={13} className={`transition-opacity duration-500 ${isLight ? 'opacity-30 text-slate-500' : 'opacity-90 text-slate-100'}`} strokeWidth={1.8}/>
      </span>

      {/* Liquid blob thumb */}
      <span
        className="absolute top-1/2 -translate-y-1/2 h-7 w-7 rounded-full theme-liquid-thumb z-10"
        style={{
          left: isLight ? 'calc(100% - 32px)' : '4px',
          transition:
            'left 650ms cubic-bezier(0.68, -0.25, 0.265, 1.35), ' +
            'background 500ms ease, box-shadow 500ms ease, transform 500ms ease',
          background: isLight
            ? 'radial-gradient(circle at 30% 30%, #FFF7CF, #FFD54A 60%, #FFB300 100%)'
            : 'radial-gradient(circle at 30% 30%, #E7EDF3, #B4FF00 60%, #7FBF00 100%)',
          boxShadow: isLight
            ? '0 0 18px rgba(255, 193, 7, 0.6), inset 0 1px 2px rgba(255,255,255,0.6)'
            : '0 0 18px rgba(180, 255, 0, 0.55), inset 0 1px 2px rgba(255,255,255,0.3)',
        }}
      />

      {/* Micro-highlight (liquid feel) */}
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none rounded-full"
        style={{
          background:
            'linear-gradient(to bottom, rgba(255,255,255,0.12), rgba(255,255,255,0) 40%, rgba(0,0,0,0.15))',
        }}
      />
    </button>
  );
}
